/**
 * 统一 SVG 图标库（PRD §3.2 / §4.4 / §6 / §7 / §8）
 *
 * 设计原则（参考微信 tab 视觉规范）：
 * - 24×24 viewBox，stroke 1.8，round line cap/join —— 微信"线条粗、几何感"风格；
 * - icon 与文字垂直排列，间距 2-4px；
 * - 选中态由父容器控制（主色 + icon 描边变粗 / 填充背景），icon 本身不变；
 * - 不引入第三方 icon 库，减小 bundle；
 * - aria-hidden 默认 true，由包裹的 button 提供 a11y label。
 */

import React from 'react';

export type IconProps = React.SVGProps<SVGSVGElement> & { size?: number };

const baseProps = (size: number | undefined): React.SVGProps<SVGSVGElement> => ({
  width: size ?? 24,
  height: size ?? 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
});

export const SearchIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.6-3.6" />
  </svg>
);

/** 帮助文档入口。 */
export const HelpIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M9.8 9.2a2.4 2.4 0 1 1 3.8 1.9c-.9.6-1.6 1-1.6 2.1" />
    <circle cx="12" cy="16.5" r="0.8" fill="currentColor" stroke="none" />
  </svg>
);

export const UserIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="M4 20c1.6-3.4 4.6-5 8-5s6.4 1.6 8 5" />
    <circle cx="12" cy="8.5" r="3.6" />
  </svg>
);

/** 时间相册 —— 微信风格：胶片孔顶条 + 中央照片缩略图。 */
export const AlbumIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <rect x="3.2" y="5.4" width="17.6" height="13.6" rx="2.2" />
    <line x1="3.2" y1="10.2" x2="20.8" y2="10.2" />
    <circle cx="7" cy="7.8" r="0.7" fill="currentColor" stroke="none" />
    <circle cx="11" cy="7.8" r="0.7" fill="currentColor" stroke="none" />
    <circle cx="13" cy="7.8" r="0.7" fill="currentColor" stroke="none" />
    <circle cx="17" cy="7.8" r="0.7" fill="currentColor" stroke="none" />
    <rect x="7.4" y="12.4" width="9.2" height="4.8" rx="0.8" />
  </svg>
);

/** 那些话 —— 微信风格：消息气泡 + 内部三个圆点 + 左下尾巴。 */
export const LetterIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="M5 4.6h14a2 2 0 0 1 2 2v10.2a2 2 0 0 1-2 2h-9.2l-4.4 3.6v-3.6H5a2 2 0 0 1-2-2V6.6a2 2 0 0 1 2-2z" />
    <circle cx="9" cy="11.6" r="1" fill="currentColor" stroke="none" />
    <circle cx="12" cy="11.6" r="1" fill="currentColor" stroke="none" />
    <circle cx="15" cy="11.6" r="1" fill="currentColor" stroke="none" />
  </svg>
);

/** 打开信件：用于进入已投入信件列表的明确动作图标。 */
export const OpenLetterIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="M3.5 7.2 12 13l8.5-5.8" />
    <path d="M4.5 5.2h15c.8 0 1.5.7 1.5 1.5v10.6c0 .8-.7 1.5-1.5 1.5h-15c-.8 0-1.5-.7-1.5-1.5V6.7c0-.8.7-1.5 1.5-1.5z" />
    <path d="m3.6 18.4 5.7-5M20.4 18.4l-5.7-5" />
  </svg>
);

/** 信件投入瓶中：用于进入信件编辑与投入流程的明确动作图标。 */
export const BottleLetterIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="M9 3.5h6M10 3.5v3l-2 2.3v9.8c0 1.2 1 2.1 2.1 2.1h3.8c1.1 0 2.1-.9 2.1-2.1V8.8L14 6.5v-3" />
    <rect x="10" y="10.4" width="4" height="4.8" rx=".5" />
    <path d="M12 8v2.2M10.7 9.3 12 10.6l1.3-1.3" />
  </svg>
);

/** 相识相遇 —— 两个相遇节点与星芒连线，避免与「我」的人形图标混淆。 */
export const MeetIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <circle cx="6.5" cy="13" r="2.2" />
    <circle cx="17.5" cy="13" r="2.2" />
    <path d="M8.8 13h6.4" />
    <path d="m12 4.2.9 2.1L15 7.2l-2.1.9-.9 2.1-.9-2.1L9 7.2l2.1-.9z" />
  </svg>
);

/**
 * 管理入口 —— 钥匙 + 钥匙齿，象征"权限管理 / 主人"，避免与设置（齿轮）混淆。
 * 用于底部 TabBar 第 4 项，仅 owner 可见。
 */
export const ManageIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <circle cx="8" cy="12" r="3.6" />
    <path d="M11.6 12H21" />
    <path d="M16.6 12v3.2" />
    <path d="M19.4 12v2.4" />
  </svg>
);

/** 空状态：相机极简图标。 */
export const CameraIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="M3.6 8.2c0-.8.6-1.4 1.4-1.4h2.6l1.2-1.6h4.4l1.2 1.6h2.6c.8 0 1.4.6 1.4 1.4v9.4c0 .8-.6 1.4-1.4 1.4H5c-.8 0-1.4-.6-1.4-1.4z" />
    <circle cx="12" cy="12.8" r="3.4" />
  </svg>
);

/** 插入图片。 */
export const ImageIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <rect x="3.2" y="4.2" width="17.6" height="15.6" rx="2" />
    <circle cx="8.3" cy="9" r="1.3" />
    <path d="m4.8 17 4.3-4.4 3.2 3 2.4-2.5 4.5 3.9" />
  </svg>
);

/** 主题设置。 */
export const PaletteIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="M12 4.2a7.8 7.8 0 0 0 0 15.6h1.2a1.9 1.9 0 0 0 0-3.8h-.8a1.7 1.7 0 0 1 0-3.4h2.1a5.3 5.3 0 0 0 5.3-5.3A7.8 7.8 0 0 0 12 4.2z" />
    <circle cx="7.5" cy="10" r=".8" fill="currentColor" stroke="none" />
    <circle cx="10" cy="7.5" r=".8" fill="currentColor" stroke="none" />
    <circle cx="13.5" cy="7.2" r=".8" fill="currentColor" stroke="none" />
  </svg>
);

/** 动效管理：三颗错落的星芒。 */
export const SparklesIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="m8 4 .9 2.8L11.6 8l-2.7 1.2L8 12l-1-2.8L4.4 8 7 6.8z" />
    <path d="m17 11 .8 2.2 2.2.8-2.2.8L17 17l-.8-2.2-2.2-.8 2.2-.8z" />
    <path d="m13.5 17.2.5 1.4 1.4.5-1.4.5-.5 1.4-.5-1.4-1.4-.5 1.4-.5z" />
  </svg>
);

/** 通知。 */
export const BellIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="M5.2 16.8h13.6l-1.5-2.1V10a5.3 5.3 0 0 0-10.6 0v4.7z" />
    <path d="M10 19.1a2.2 2.2 0 0 0 4 0" />
  </svg>
);

/** 插入录音。 */
export const MicIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <rect x="8.2" y="3.2" width="7.6" height="11.2" rx="3.8" />
    <path d="M5.2 11.8a6.8 6.8 0 0 0 13.6 0M12 18.6v2.2M8.5 20.8h7" />
  </svg>
);

/** 相识相遇入口：便签轮廓 + 相遇节点，作为首页顶栏的小型入口图标。 */
export const MeetMemoIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="M6 3.8h8.6L19 8.2v11.1c0 .5-.4.9-.9.9H6c-.5 0-.9-.4-.9-.9V4.7c0-.5.4-.9.9-.9z" />
    <path d="M14.4 3.8v4.4H19" />
    <path d="M8.2 12.2h3.1M8.2 15.2h4.9" />
    <circle cx="15.6" cy="13" r="1.3" />
    <circle cx="18.2" cy="15.5" r="1.3" />
    <path d="m16.5 13.9 1 1" />
  </svg>
);

/** 空状态：垃圾桶。 */
export const TrashIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="M4.4 7.2h15.2" />
    <path d="M9.2 4h5.6v2.4H9.2z" />
    <path d="M6 7.2v12.4c0 .8.6 1.4 1.4 1.4h9.2c.8 0 1.4-.6 1.4-1.4V7.2" />
    <path d="M10 10.8v6.4M14 10.8v6.4" />
  </svg>
);

/** 编辑铅笔。 */
export const PencilIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="M14.6 4.8l4.6 4.6-9.2 9.2H5v-5z" />
    <path d="M13.4 6l4.6 4.6" />
  </svg>
);

/** 相伴天数旁的双心形标记（替换原 💞 emoji）。 */
export const HeartPairIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="M8.4 9.6c-.8-1.2-2.6-1.2-3.4 0-.8 1.2-.4 2.6.4 3.4l3 3 3-3c.8-.8 1.2-2.2.4-3.4-.8-1.2-2.6-1.2-3.4 0z" />
    <path d="M16.8 7.6c-.8-1.2-2.6-1.2-3.4 0-.8 1.2-.4 2.6.4 3.4l3 3 3-3c.8-.8 1.2-2.2.4-3.4-.8-1.2-2.6-1.2-3.4 0z" />
  </svg>
);

/** 返回箭头（←）。 */
export const ArrowLeftIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="M15.4 5.6 9 12l6.4 6.4" />
  </svg>
);

/** 首页（Home）入口。 */
export const HomeIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="m3.8 10.8 8.2-6.6 8.2 6.6" />
    <path d="M5.8 9.8v9.4h12.4V9.8" />
    <path d="M9.6 19.2v-5h4.8v5" />
  </svg>
);

/** 关闭（×）。 */
export const CloseIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="m6 6 12 12M6 18 18 6" />
  </svg>
);

/**
 * 审批中心 —— 入信箱（顶部带勾的矩形 inbox），避免与设置（齿轮）/管理（钥匙）混淆。
 * 用于 /manage/approvals 入口卡片与顶栏小徽标。
 */
export const ApprovalIcon: React.FC<IconProps> = ({ size, ...rest }) => (
  <svg {...baseProps(size)} {...rest}>
    <path d="M3.6 12.4l2.4-7.4c.2-.6.7-1 1.4-1h9.2c.6 0 1.2.4 1.4 1l2.4 7.4" />
    <path d="M3.6 12.4v5.8c0 .8.6 1.4 1.4 1.4h14c.6 0 1.4-.6 1.4-1.4v-5.8" />
    <path d="M3.6 12.4h4.4l1.4 2h5.2l1.4-2H21" />
  </svg>
);

export default {
  SearchIcon,
  HelpIcon,
  UserIcon,
  AlbumIcon,
  LetterIcon,
  OpenLetterIcon,
  BottleLetterIcon,
  MeetIcon,
  ManageIcon,
  CameraIcon,
  ImageIcon,
  SparklesIcon,
  MicIcon,
  MeetMemoIcon,
  TrashIcon,
  PencilIcon,
  HeartPairIcon,
  ArrowLeftIcon,
  HomeIcon,
  CloseIcon,
  ApprovalIcon,
};
